import { useCallback, useMemo } from 'react'
import { reorder } from '../dashboard/reorder'
import { slugify } from '../dashboard/slug'
import { DEFAULT_TABS, TabDefinition, TabIconKey, isDefaultTab, isPermanentTab } from '../dashboard/tabs'
import { clearWindowLayout } from './useLocalWindowLayout'
import { usePersistedState } from './localStore'
import { attributionStamp } from './attribution'

const TABS_KEY = 'advanced.workspaceTabs.v1'

export type WorkspaceTabsState = {
  tabs: TabDefinition[]
  addTab: (label: string, icon: TabIconKey) => void
  removeTab: (id: string) => void
  setVisible: (id: string, visible: boolean) => void
  moveTo: (from: number, to: number) => void
}

const RETIRED_SHIPPED_TAB_IDS = ['patientDetails', 'chat', 'custom']

const reconcileSavedTabs = (saved: TabDefinition[]): TabDefinition[] => {
  const kept = saved.filter(
    ({ id, kind, createdBy }) =>
      (process.env.NODE_ENV === 'development' || kind !== 'progress') &&
      !(RETIRED_SHIPPED_TAB_IDS.includes(id) && createdBy === DEFAULT_TABS[0].createdBy)
  )
  const reiconed = kept.map((tab) => {
    const icon = DEFAULT_TABS.find(({ id }) => id === tab.id)?.icon
    return icon && icon !== tab.icon ? { ...tab, icon } : tab
  })
  const missing = DEFAULT_TABS.filter((tab) => isPermanentTab(tab.id) && !kept.some(({ id }) => id === tab.id))
  const unchanged = missing.length === 0 && kept.length === saved.length && reiconed.every((tab, i) => tab === kept[i])
  return unchanged ? saved : [...reiconed, ...missing]
}

export const useWorkspaceTabs = (): WorkspaceTabsState => {
  const [saved, setTabs] = usePersistedState<TabDefinition[]>(TABS_KEY, DEFAULT_TABS)
  const tabs = useMemo(() => reconcileSavedTabs(saved), [saved])

  const addTab = useCallback(
    (label: string, icon: TabIconKey) => {
      const id = slugify(label, new Set(tabs.map((tab) => tab.id)))
      const { submittedBy, submittedAt } = attributionStamp()
      const tab: TabDefinition = {
        id,
        label: label.trim() || 'Untitled tab',
        icon,
        kind: 'window',
        visible: true,
        createdAt: submittedAt,
        createdBy: submittedBy,
      }
      setTabs([...tabs, tab])
    },
    [setTabs, tabs]
  )

  // Also drops the tab's saved layout, unlike `setVisible` — not undoable from "Manage tabs".
  const removeTab = useCallback(
    (id: string) => {
      if (isDefaultTab(id)) {
        return
      }
      const tab = tabs.find((candidate) => candidate.id === id)
      if (tab?.kind === 'window') {
        clearWindowLayout(id)
      }
      setTabs(tabs.filter((candidate) => candidate.id !== id))
    },
    [setTabs, tabs]
  )

  const setVisible = useCallback(
    (id: string, visible: boolean) => {
      if (isPermanentTab(id) && !visible) {
        return
      }
      setTabs(tabs.map((tab) => (tab.id === id ? { ...tab, visible } : tab)))
    },
    [setTabs, tabs]
  )

  const moveTo = useCallback(
    (from: number, to: number) => {
      const next = reorder(tabs, from, to)
      if (next !== tabs) {
        setTabs(next)
      }
    },
    [setTabs, tabs]
  )

  return { tabs, addTab, removeTab, setVisible, moveTo }
}
